"use client";

import { useRef } from "react";
import { m, useScroll, useTransform } from "motion/react";
import { useIsMobile } from "@marketing-sites/hooks/use-mobile";
import { cn } from "@marketing-sites/utils/cn";
import { Section, type SectionProps } from "../../primitives";
import { VideoPlayer, type VideoPlayerProps } from "../video-player";

/** `tags` and `dotDecorators` are left out on purpose: a labelled or decorated
 *  video section has no precedent on either site, and the video's own title
 *  draws over its poster. */
export type VideoSectionProps = Pick<
  SectionProps,
  "id" | "gridCols" | "className"
> & {
  analyticsRegion: string;
  sectionMetadata: { analyticsId: string };
  videoMetadata: { analyticsId: string };
  scaleOnScroll?: boolean;
} & Pick<
    VideoPlayerProps,
    "video" | "previewVideo" | "overlay" | "controls" | "priority"
  >;

function easeOutQuad(v: number) {
  return 1 - Math.pow(1 - v, 2);
}

export default function VideoSection({
  analyticsRegion,
  sectionMetadata,
  videoMetadata,
  video,
  previewVideo,
  overlay,
  controls,
  priority,
  gridCols = "10",
  className,
  scaleOnScroll,
  ...section
}: VideoSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  const animateOnScroll = scaleOnScroll && !isMobile;

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "start 0.1"],
  });

  const scale = useTransform(
    scrollYProgress,
    (v) => 0.75 + 0.25 * easeOutQuad(v),
  );
  const opacity = useTransform(scrollYProgress, [0, 1], [0.8, 1]);
  const y = useTransform(scrollYProgress, [0, 1], [50, 0]);

  return (
    <Section
      {...section}
      ref={sectionRef}
      data-analytics-region={analyticsRegion}
      data-analytics-id={sectionMetadata.analyticsId}
      gridCols={gridCols}
      dotDecorators={false}
      className={cn("py-6 md:py-12", className)}
    >
      <m.div
        className="col-span-full"
        style={
          animateOnScroll
            ? { scale, opacity, y }
            : { scale: 1, opacity: 1, y: 0 }
        }
      >
        <VideoPlayer
          analyticsId={videoMetadata.analyticsId}
          video={video}
          previewVideo={previewVideo}
          overlay={overlay}
          controls={controls}
          priority={priority}
          className="relative aspect-video w-full overflow-hidden"
        />
      </m.div>
    </Section>
  );
}
